import React, { useEffect, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import ReactQuill from 'react-quill';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import 'react-quill/dist/quill.snow.css';
import { toast } from '@/components/ui/toast';
import {
  Check,
  ChevronDown,
  ChevronRight,
  Edit2,
  Info,
  Loader2,
  Search,
  Star,
  StarOff,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useUser } from '@/store/useUser';

const GET_SERVER_CONFIG = gql`
  query GetServerConfig {
    getServerConfig {
      loginNewsEnabled
      loginNewsTitle
      loginNewsContent
      dashboardAnnouncementsEnabled
      dashboardAnnouncementsTitle
      dashboardAnnouncementsContent
      dashboardAnnouncementsStatus
    }
  }
`;

const UPDATE_SERVER_CONFIG = gql`
  mutation UpdateServerConfig($updates: ConfigSettingsInput) {
    updateServerConfig(updates: $updates) {
      message
      responseCode
    }
  }
`;

interface SettingRow {
  key: string;
  label: string;
  value: any;
  type: 'text' | 'boolean' | 'number' | 'email' | 'html' | 'longtext' | 'select';
  options?: { label: string; value: string }[];
  description?: string;
  placeholder?: string;
}

interface SettingSection {
  title: string;
  expanded: boolean;
  settings: SettingRow[];
}

interface HtmlEditorProps {
  label: string;
  value: string;
  description?: string;
  onChange: (value: string) => void;
}

interface SettingRowComponentProps {
  setting: SettingRow;
  onChange: (key: string, value: any) => void | Promise<void>;
  isSaving: boolean;
  isFavorite: boolean;
  onToggleFavorite: (settingKey: string) => void | Promise<void>;
}

const HtmlEditor = ({ label, value, description, onChange }: HtmlEditorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localValue, setLocalValue] = useState(value || '');

  useEffect(() => {
    if (isOpen) {
      setLocalValue(value || '');
    }
  }, [isOpen, value]);

  const handleSave = () => {
    onChange(localValue);
    setIsOpen(false);
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="justify-start"
        onClick={() => setIsOpen(true)}
      >
        <Edit2 className="h-3.5 w-3.5" />
        Edit HTML
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent
          className="flex h-full flex-col gap-0 overflow-hidden p-0"
          contentWidth="92vw"
          contentMaxWidth="1100px"
          contentHeight="85vh"
          contentMaxHeight="85vh"
        >
          <DialogHeader className="gap-1 border-b border-[var(--neutral-gray-30)] px-5 py-4">
            <DialogTitle>{label}</DialogTitle>
            <DialogDescription>
              {description || 'Update the HTML announcement content for this setting.'}
            </DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
            <ReactQuill value={localValue} onChange={setLocalValue} theme="snow" style={{ height: '400px' }} />
          </div>
          <DialogFooter className="border-t border-[var(--neutral-gray-30)] px-5 py-4">
            <Button type="button" variant="outline" size="sm" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button type="button" size="sm" onClick={handleSave}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const SettingRowComponent = ({
  setting,
  onChange,
  isSaving,
  isFavorite,
  onToggleFavorite,
}: SettingRowComponentProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState<any>(setting.value ?? '');

  useEffect(() => {
    setLocalValue(setting.value ?? '');
  }, [setting.value]);

  const handleSave = () => {
    void onChange(setting.key, localValue);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setLocalValue(setting.value ?? '');
    setIsEditing(false);
  };

  const renderValue = () => {
    if (setting.type === 'boolean') {
      return (
        <div className="flex items-center gap-3">
          <Switch
            aria-label={setting.label}
            checked={Boolean(setting.value)}
            disabled={isSaving}
            className="data-[state=checked]:bg-[var(--azure-primary)] data-[state=unchecked]:bg-[var(--neutral-gray-50)]"
            onCheckedChange={(checked) => {
              void onChange(setting.key, checked);
            }}
          />
          <span className="text-xs font-medium text-[var(--neutral-gray-90)]">
            {setting.value ? 'Enabled' : 'Disabled'}
          </span>
        </div>
      );
    }

    if (setting.type === 'html') {
      return (
        <HtmlEditor
          label={setting.label}
          value={setting.value || ''}
          description={setting.description}
          onChange={(nextValue) => {
            void onChange(setting.key, nextValue);
          }}
        />
      );
    }

    if (isEditing && setting.type === 'select') {
      const selectValue = localValue === '' || localValue === null || localValue === undefined
        ? undefined
        : String(localValue);

      return (
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <Select value={selectValue} onValueChange={(value) => setLocalValue(value)}>
            <SelectTrigger className="min-w-[220px] max-w-[320px]">
              <SelectValue placeholder="Select a value" />
            </SelectTrigger>
            <SelectContent>
              {(setting.options || []).map((option) => (
                <SelectItem key={option.value} value={option.value} className="capitalize">
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="button"
            size="sm"
            className="w-8 px-0"
            disabled={isSaving}
            onClick={handleSave}
            aria-label={`Save ${setting.label}`}
          >
            <Check className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-8 px-0"
            disabled={isSaving}
            onClick={handleCancel}
            aria-label={`Cancel editing ${setting.label}`}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      );
    }

    if (isEditing) {
      return (
        <div className="flex min-w-0 flex-1 items-start gap-2">
          {setting.type === 'longtext' ? (
            <Textarea
              rows={4}
              value={localValue ?? ''}
              placeholder={setting.placeholder}
              onChange={(event) => setLocalValue(event.target.value)}
            />
          ) : (
            <Input
              type={setting.type}
              value={localValue ?? ''}
              placeholder={setting.placeholder}
              onChange={(event) => setLocalValue(event.target.value)}
            />
          )}
          <div className="flex items-center gap-2 pt-0.5">
            <Button
              type="button"
              size="sm"
              className="w-8 px-0"
              disabled={isSaving}
              onClick={handleSave}
              aria-label={`Save ${setting.label}`}
            >
              <Check className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-8 px-0"
              disabled={isSaving}
              onClick={handleCancel}
              aria-label={`Cancel editing ${setting.label}`}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      );
    }

    const displayValue = setting.type === 'select'
      ? (setting.options || []).find((option) => option.value === setting.value)?.label || setting.value
      : setting.value;

    const hasDisplayValue = displayValue !== '' && displayValue !== null && displayValue !== undefined;

    return (
      <div className="flex min-w-0 items-center gap-2">
        <span className="min-w-0 text-sm text-[var(--neutral-gray-120)]">
          {hasDisplayValue ? displayValue : <span className="italic text-[var(--neutral-gray-80)]">Not set</span>}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="w-8 px-0 text-[var(--neutral-gray-80)]"
          disabled={isSaving}
          onClick={() => setIsEditing(true)}
          aria-label={`Edit ${setting.label}`}
        >
          <Edit2 className="h-4 w-4" />
        </Button>
        {isSaving && <Loader2 className="h-4 w-4 animate-spin text-[var(--azure-primary)]" />}
      </div>
    );
  };

  return (
    <div className="group/setting relative border-t border-[var(--neutral-gray-30)] px-4 py-3 first:border-t-0">
      <div className="flex flex-col gap-3 pr-8 lg:flex-row lg:items-start lg:gap-6">
        <div className="min-w-0 lg:w-[360px] lg:flex-none">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-[var(--neutral-gray-120)]">{setting.label}</label>
            {setting.description && (
              <div className="group/info relative">
                <Info className="h-3.5 w-3.5 text-[var(--neutral-gray-80)]" />
                <div className="absolute bottom-full left-0 z-10 mb-2 hidden w-72 rounded-[var(--border-radius-medium)] bg-[var(--neutral-gray-120)] px-3 py-2 text-xs leading-5 text-white shadow-[var(--shadow-medium)] group-hover/info:block">
                  {setting.description}
                </div>
              </div>
            )}
          </div>
          <div className="mt-1 text-xs font-mono text-[var(--neutral-gray-80)]">{setting.key}</div>
        </div>
        <div className="min-w-0 flex-1">{renderValue()}</div>
      </div>

      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={`absolute right-2 top-2 w-8 px-0 ${
          isFavorite
            ? 'text-[var(--warning-strong)]'
            : 'text-[var(--neutral-gray-70)] opacity-0 group-hover/setting:opacity-100'
        }`}
        onClick={(event) => {
          event.stopPropagation();
          void onToggleFavorite(setting.key);
        }}
        title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
        aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
      >
        {isFavorite ? <Star className="h-4 w-4 fill-current" /> : <StarOff className="h-4 w-4" />}
      </Button>
    </div>
  );
};

export const NewsSettings = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [savingKeys, setSavingKeys] = useState<Set<string>>(new Set());
  const [localFavorites, setLocalFavorites] = useState<string[]>([]);
  const [sections, setSections] = useState<SettingSection[]>([]);

  const { loading, error, data } = useQuery(GET_SERVER_CONFIG);
  const [updateServerConfig] = useMutation(UPDATE_SERVER_CONFIG);
  const { user, updateSystemSettingsFavorites } = useUser();

  useEffect(() => {
    if (!user?.settings?.systemSettingsFavorites) {
      return;
    }

    setLocalFavorites(user.settings.systemSettingsFavorites);
  }, [user]);

  const toggleFavorite = async (settingKey: string) => {
    const nextFavorites = localFavorites.includes(settingKey)
      ? localFavorites.filter((key) => key !== settingKey)
      : [...localFavorites, settingKey];

    setLocalFavorites(nextFavorites);

    const result = await updateSystemSettingsFavorites(nextFavorites);
    if (result.success) {
      return;
    }

    console.error('Failed to update favorites:', result.error);
    toast.error('Failed to update favorites');
  };

  useEffect(() => {
    if (!data) {
      return;
    }

    const config = data.getServerConfig;
    const nextSections: SettingSection[] = [
      {
        title: 'Login News',
        expanded: true,
        settings: [
          {
            key: 'loginNewsEnabled',
            label: 'Login News Display',
            value: config.loginNewsEnabled,
            type: 'boolean',
            description: 'Show announcements on the login page.',
          },
          {
            key: 'loginNewsTitle',
            label: 'Login News Title',
            value: config.loginNewsTitle,
            type: 'text',
            placeholder: 'System Announcements',
            description: 'Title shown above the login page announcement block.',
          },
          {
            key: 'loginNewsContent',
            label: 'Login Page News Content',
            value: config.loginNewsContent,
            type: 'html',
            description: 'HTML content displayed on the login page.',
          },
        ],
      },
      {
        title: 'Dashboard Announcements',
        expanded: true,
        settings: [
          {
            key: 'dashboardAnnouncementsEnabled',
            label: 'Dashboard Announcements Display',
            value: config.dashboardAnnouncementsEnabled,
            type: 'boolean',
            description: 'Show announcements on the dashboard after login.',
          },
          {
            key: 'dashboardAnnouncementsTitle',
            label: 'Dashboard Announcements Title',
            value: config.dashboardAnnouncementsTitle,
            type: 'text',
            placeholder: 'System Updates',
            description: 'Title shown above the dashboard announcement block.',
          },
          {
            key: 'dashboardAnnouncementsContent',
            label: 'Dashboard Announcements Content',
            value: config.dashboardAnnouncementsContent,
            type: 'html',
            description: 'HTML content displayed on the dashboard.',
          },
          {
            key: 'dashboardAnnouncementsStatus',
            label: 'Dashboard Warn Level',
            value: config.dashboardAnnouncementsStatus || 'warning',
            type: 'select',
            description: 'Controls the visual tone of the dashboard announcement.',
            options: [
              { label: 'Warn', value: 'warning' },
              { label: 'Alarm', value: 'alarm' },
            ],
          },
        ],
      },
    ];

    setSections(nextSections);
  }, [data]);

  const handleSettingChange = async (key: string, value: any) => {
    setSections((previousSections) =>
      previousSections.map((section) => ({
        ...section,
        settings: section.settings.map((setting) => (setting.key === key ? { ...setting, value } : setting)),
      })),
    );

    setSavingKeys((previousKeys) => {
      const nextKeys = new Set(previousKeys);
      nextKeys.add(key);
      return nextKeys;
    });

    try {
      const parsedValue = value === 'true' ? true : value === 'false' ? false : value;
      const updates = { [key]: parsedValue };
      const response = await updateServerConfig({ variables: { updates } });
      const updateResult = response?.data?.updateServerConfig;

      if (updateResult?.responseCode === 0) {
        toast.success(`Setting updated: ${key}`);
      }

      if (updateResult?.responseCode !== 0) {
        toast.error(`Failed to update: ${key} - ${updateResult?.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error updating setting:', err);
      toast.error(`Failed to update: ${key}`);
    } finally {
      setSavingKeys((previousKeys) => {
        const nextKeys = new Set(previousKeys);
        nextKeys.delete(key);
        return nextKeys;
      });
    }
  };

  const toggleSection = (title: string) => {
    setSections((previousSections) =>
      previousSections.map((section) =>
        section.title === title ? { ...section, expanded: !section.expanded } : section,
      ),
    );
  };

  const filteredSections = (() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const visibleSections = sections
      .map((section) => ({
        ...section,
        settings: section.settings.filter((setting) => {
          if (!normalizedSearch) {
            return true;
          }

          const matchesLabel = setting.label.toLowerCase().includes(normalizedSearch);
          const matchesKey = setting.key.toLowerCase().includes(normalizedSearch);
          const matchesDescription = setting.description?.toLowerCase().includes(normalizedSearch);
          return matchesLabel || matchesKey || matchesDescription;
        }),
      }))
      .filter((section) => !normalizedSearch || section.settings.length > 0);

    if (localFavorites.length === 0 || normalizedSearch) {
      return visibleSections;
    }

    const favoriteSettings: SettingRow[] = [];
    sections.forEach((section) => {
      section.settings.forEach((setting) => {
        if (!localFavorites.includes(setting.key)) {
          return;
        }

        favoriteSettings.push(setting);
      });
    });

    if (favoriteSettings.length === 0) {
      return visibleSections;
    }

    return [
      {
        title: 'Favorites',
        expanded: true,
        settings: favoriteSettings,
      },
      ...visibleSections,
    ];
  })();

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--azure-primary)]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 md:p-5">
        <Card className="border-[var(--semantic-error)]/20">
          <CardContent className="p-4 pt-4">
            <p className="text-sm text-[var(--semantic-error)]">Error loading configuration: {error.message}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--surface-app)] p-4 md:p-5">
      <div className="mx-auto flex max-w-[1600px] flex-col gap-4">
        <Card>
          <CardHeader className="gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="space-y-1">
              <CardTitle className="text-xl">News & Announcements</CardTitle>
              <CardDescription>
                Manage login-page news and dashboard announcement content.
              </CardDescription>
            </div>
            <div className="flex w-full flex-col gap-2 lg:max-w-[340px]">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--neutral-gray-80)]" />
                <Input
                  type="text"
                  value={searchTerm}
                  placeholder="Search settings"
                  className="pl-9"
                  onChange={(event) => setSearchTerm(event.target.value)}
                />
              </div>
              {savingKeys.size > 0 && (
                <div className="inline-flex items-center gap-2 text-xs font-medium text-[var(--azure-primary)]">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Saving changes
                </div>
              )}
            </div>
          </CardHeader>
        </Card>

        <Card className="overflow-hidden">
          {filteredSections.length === 0 && (
            <CardContent className="p-5 pt-5">
              <p className="text-sm text-[var(--neutral-gray-80)]">No settings match your search.</p>
            </CardContent>
          )}

          {filteredSections.map((section) => {
            const isAlwaysExpanded = section.title === 'Favorites';
            const isExpanded = isAlwaysExpanded || section.expanded;

            return (
              <section key={section.title} className="border-t border-[var(--neutral-gray-30)] first:border-t-0">
                {isAlwaysExpanded ? (
                  <div className="flex items-center justify-between bg-[var(--surface-muted)] px-4 py-3">
                    <div className="flex min-w-0 items-center gap-2">
                      <h2 className="text-sm font-semibold text-[var(--neutral-gray-120)]">{section.title}</h2>
                      <span className="text-xs text-[var(--neutral-gray-80)]">
                        ({section.settings.length} settings)
                      </span>
                    </div>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-auto w-full justify-between rounded-none bg-[var(--surface-muted)] px-4 py-3 hover:bg-[var(--neutral-gray-20)]"
                    onClick={() => toggleSection(section.title)}
                  >
                    <div className="flex min-w-0 items-center gap-2 text-left">
                      <h2 className="text-sm font-semibold text-[var(--neutral-gray-120)]">{section.title}</h2>
                      <span className="text-xs font-normal text-[var(--neutral-gray-80)]">
                        ({section.settings.length} settings)
                      </span>
                    </div>
                    {section.expanded ? (
                      <ChevronDown className="h-4 w-4 text-[var(--neutral-gray-80)]" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-[var(--neutral-gray-80)]" />
                    )}
                  </Button>
                )}

                {isExpanded && (
                  <div>
                    {section.settings.map((setting) => (
                      <SettingRowComponent
                        key={`${section.title}-${setting.key}`}
                        setting={setting}
                        onChange={handleSettingChange}
                        isSaving={savingKeys.has(setting.key)}
                        isFavorite={localFavorites.includes(setting.key)}
                        onToggleFavorite={toggleFavorite}
                      />
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </Card>
      </div>
    </div>
  );
};

export default NewsSettings;
